import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { SelectChangeEvent } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '../../../../theme/colors';
import { fontSize, fontWeight, TypographyStyledGlobal } from '../../../../theme/font';
import { TemporalScopeFromApi } from '../../dashboardInterfaces';
import { StyledMenuItem, StyledSelectFormControl } from '../../styled';
import { ButtonActionsStyled, MenuItemStyled, SelectFilterStyled } from './styled';

interface TemporalScopeSelectProps {
    temporal: TemporalScopeFromApi;
    handleSelectedScopeLabel: ([scopeId, scopeLabel, modalitiesLabel]: [number, string, string | null]) => void;
    handleSelectedScope: ([scopeId, modalityId]: [string, Array<string> | null]) => void;
    selectedScope: any;
    disabled?: boolean;
    unavailableModalityIds?: Array<string>;
}

const TemporalScopeSelect: FC<TemporalScopeSelectProps> = ({ temporal, handleSelectedScope, handleSelectedScopeLabel, selectedScope, disabled = false, unavailableModalityIds = [] }) => {
    const { t } = useTranslation('dashboard');
    const [openFilterSelect, setOpenFilterSelect] = useState(false);

    const menuItems = Object.entries(temporal.modalities).map((modality) => (
        <MenuItemStyled
            key={modality[0]}
            // Already taken by the other period, comparing it with itself would
            // tell nothing.
            disabled={unavailableModalityIds.includes(modality[0])}
            value={modality[0]}
            disableRipple
        >
            {modality[1]}
        </MenuItemStyled>
    ));

    const handleChange = (event: SelectChangeEvent<typeof selectedScope>) => {
        const {
            target: { value },
        } = event;
        if (value.includes('null')) {
            handleSelectedScope([Object.keys(temporal.scope)[0], null]);
            handleSelectedScopeLabel([parseInt(Object.keys(temporal.scope)[0], 10), Object.values(temporal.scope)[0], null]);
        } else {
            handleSelectedScope([Object.keys(temporal.scope)[0], value as Array<string>]);
            handleSelectedScopeLabel([parseInt(Object.keys(temporal.scope)[0], 10), Object.values(temporal.scope)[0], Object.entries(temporal.modalities).filter((modality) => value.includes(modality[0])).map((modality) => modality[1]).join(', ') as string]);
        }
    };

    const closeSelectFilter = (e: React.ChangeEvent) => {
        const targetRole = e.target.getAttribute('role');
        if (targetRole === 'option' && e.target.getAttribute('data-value') !== 'null') {
            setOpenFilterSelect(true);

            return;
        }
        if (targetRole === 'button') {
            setOpenFilterSelect(true);

            return;
        }

        setOpenFilterSelect(false);
    };

    return (
        <StyledSelectFormControl sx={{ m: 1, minWidth: 120 }}>
            <ButtonActionsStyled>
                <TypographyStyledGlobal
                    color={disabled ? colors.grey400 : colors.grey200}
                    size={fontSize.sm}
                    weight={fontWeight.regular}
                >
                    {Object.values(temporal.scope)[0]}
                </TypographyStyledGlobal>
            </ButtonActionsStyled>

            <SelectFilterStyled
                IconComponent={ArrowDropDownRoundedIcon}
                disabled={disabled}
                onChange={handleChange}
                onClick={closeSelectFilter}
                open={disabled ? false : openFilterSelect}
                renderValue={(selected: Array<string>) => {
                    if (selected.length === 0) {
                        return t('filter.selectFilter');
                    }

                    return Object.entries(temporal.modalities)
                        .filter((modality) => selected.includes(modality[0]))
                        .map((modality) => modality[1])
                        .join(', ');
                }}
                sx={(selectedScope.length > 0 && !disabled) ? { color: colors.grey100 } : { color: colors.grey400 }}
                value={selectedScope[0] && selectedScope[0].modalityId ? selectedScope[0].modalityId : []}
                variant="standard"
                displayEmpty
                multiple
            >
                <StyledMenuItem sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.regular }} value='placeholder' disabled>
                    {t('filter.selectFilter')}
                </StyledMenuItem>
                <StyledMenuItem onClick={() => handleSelectedScope([Object.keys(temporal.scope)[0], null])} sx={{ fontSize: fontSize.sm, color: colors.grey400, fontStyle: 'italic' }} value='null'>
                    {t('filter.noFilter')}
                </StyledMenuItem>
                {menuItems}
            </SelectFilterStyled>

        </StyledSelectFormControl>
    );
};

export default TemporalScopeSelect;
